import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, AlertCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import '../styles/Login.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const userId = searchParams.get('id');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token || !userId) {
        setTokenError('Invalid reset link. Please request a new password reset.');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/auth/verify-reset-token?token=${encodeURIComponent(token)}&id=${encodeURIComponent(userId)}`
        );
        const data = await response.json();
        
        if (data.valid) {
          setTokenValid(true);
        } else {
          setTokenError(data.reason || 'Invalid or expired reset link.');
        }
      } catch (err) {
        setTokenError('Unable to verify reset link. Please try again.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          user_id: userId,
          new_password: password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state while verifying token
  if (isVerifying) {
    return (
      <div className="login-page">
        <section className="auth-section">
          <div className="container">
            <div className="auth-container">
              <div className="auth-form" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <img 
                  src="/cadence-logo-gold.png" 
                  alt="CADENCE" 
                  style={{ width: '80px', marginBottom: '1.5rem' }}
                />
                <div className="loading-spinner" style={{ margin: '1rem auto' }}></div>
                <p style={{ color: 'var(--text-secondary)' }}>Verifying your reset link...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid && !isVerifying) {
    return (
      <div className="login-page">
        <section className="auth-section">
          <div className="container">
            <div className="auth-container">
              <div className="auth-form" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <img 
                  src="/cadence-logo-gold.png" 
                  alt="CADENCE" 
                  style={{ width: '80px', marginBottom: '1.5rem' }}
                />
                <AlertCircle size={48} color="#EF4444" style={{ marginBottom: '1rem' }} />
                <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                  Invalid Reset Link
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  {tokenError}
                </p>
                <Link to="/forgot-password" className="btn-primary" style={{ display: 'inline-block', marginBottom: '1rem' }}>
                  Request New Reset Link
                </Link>
                <br />
                <Link to="/login" style={{ color: 'var(--brand-gold)', fontSize: '0.9375rem' }}>
                  Back to Login
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="login-page">
        <section className="auth-section">
          <div className="container">
            <div className="auth-container">
              <div className="auth-form" style={{ textAlign: 'center', padding: '3rem 2rem' }}>
                <img 
                  src="/cadence-logo-gold.png" 
                  alt="CADENCE" 
                  style={{ width: '80px', marginBottom: '1.5rem' }}
                />
                <CheckCircle size={48} color="#D4AF37" style={{ marginBottom: '1rem' }} />
                <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>
                  Password Reset Successful!
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem', lineHeight: '1.6' }}>
                  Your password has been updated. You can now log in with your new password.
                </p>
                <Link to="/login" className="btn-primary" style={{ display: 'inline-block' }}>
                  Go to Login
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="login-page">
      <section className="hero-mini" style={{ 
        background: 'linear-gradient(135deg, var(--brand-plum) 0%, #2D0A31 100%)',
        padding: '4rem 0 3rem',
        textAlign: 'center'
      }}>
        <div className="container">
          <img 
            src="/cadence-logo-gold.png" 
            alt="CADENCE Medical Consulting" 
            className="login-logo"
            style={{ width: '120px' }}
          />
          <h1 style={{ color: 'var(--brand-gold)', marginBottom: '0.5rem' }}>Create New Password</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>
            Enter your new password below
          </p>
        </div>
      </section>

      <section className="auth-section">
        <div className="container">
          <div className="auth-container">
            {error && (
              <div className="auth-message error">
                <AlertCircle size={20} />
                <p>{error}</p>
              </div>
            )}

            <form className="auth-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label>New Password</label>
                <div className="input-with-icon">
                  <Lock size={16} />
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button 
                    type="button" 
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm New Password</label>
                <div className="input-with-icon">
                  <Lock size={16} />
                  <input 
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={8}
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>

              <div style={{ 
                background: 'rgba(212, 175, 55, 0.1)', 
                padding: '1rem', 
                borderRadius: '0.5rem',
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)'
              }}>
                <strong style={{ color: 'var(--brand-plum)' }}>Password requirements:</strong>
                <ul style={{ margin: '0.5rem 0 0 1.25rem', paddingLeft: '0' }}>
                  <li>At least 8 characters long</li>
                  <li>We recommend using a mix of letters, numbers, and symbols</li>
                </ul>
              </div>

              <button 
                type="submit" 
                className="btn-primary auth-submit"
                disabled={isLoading}
                data-testid="reset-password-submit"
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>

              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <Link 
                  to="/login" 
                  style={{ 
                    display: 'inline-flex', 
                    alignItems: 'center', 
                    gap: '0.5rem',
                    color: 'var(--brand-gold)',
                    textDecoration: 'none',
                    fontSize: '0.9375rem'
                  }}
                >
                  <ArrowLeft size={18} />
                  Back to Login
                </Link>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPassword;
